import {
  IDestinationEntityJobRunOptions,
  IHasSourceIdentifier,
  ITranslationMap,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { Brand, toINamedDocuments } from '@principle-theorem/principle-core';
import {
  IStaffer,
  SterilisationStatus,
  type IBrand,
  type IPatient,
  type IPractice,
  type ISterilisationRecord,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  INamedDocument,
  Timestamp,
  WithPrimaryKey,
  getError,
  resolveSequentially,
  snapshotCombineLatest,
  toNamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, first } from 'lodash';
import { combineLatest, from, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientSterilisationDestinationEntity,
  ISterilisationRecordBaseJobData,
  ISterilisationRecordMigrationData,
} from '../../../destination/entities/sterilisation-records';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { PracticeIdFilter } from '../../../destination/filters/practice-id-filter';
import { ItemCodeNoteType } from '../../../mappings/item-codes-to-notes-xlsx';
import { PracticeMigration } from '../../../practice-migrations';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  IOasisPatientNote,
  OasisPatientNote,
  PatientNoteSourceEntity,
} from '../../source/entities/patient-notes';
import {
  OasisPatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { PROVIDER_RESOURCE_TYPE } from '../../source/entities/providers';
import { OasisNoteDataTransformer } from '../../source/lib/oasis-notes-data-transformer';
import { OasisPatientNoteTypeToNoteMappingHandler } from '../mappings/patient-note-types-to-notes';
import { OasisPracticeMappingHandler } from '../mappings/practices';
import { OasisStafferMappingHandler } from '../mappings/staff';
import { PatientDestinationEntity } from './patients';

interface IPatientSterilisationJobData
  extends ISterilisationRecordBaseJobData<OasisPatient> {
  brand: WithRef<IBrand>;
  practices: WithPrimaryKey<ITranslationMap<IPractice>>[];
  sourceItemCodes: WithPrimaryKey<ITranslationMap<object, ItemCodeNoteType>>[];
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  practitioners: INamedDocument<IStaffer>[];
}

export class PatientSterilisationRecordDestinationEntity extends BasePatientSterilisationDestinationEntity<
  OasisPatient,
  IPatientSterilisationJobData
> {
  override filters = [
    new PracticeIdFilter<IPatientSterilisationJobData>((jobData) =>
      jobData.sourcePatient.data.id.toString()
    ),
    new PatientIdFilter<IPatientSterilisationJobData>((jobData) =>
      this.sourceEntities.patients
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    treatmentNotes: new PatientNoteSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    noteTypes: new OasisPatientNoteTypeToNoteMappingHandler(),
    practices: new OasisPracticeMappingHandler(),
    staff: new OasisStafferMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientSterilisationJobData[]> {
    const sourceItemCodes$ =
      this.customMappings.noteTypes.getRecords(translationMap);
    const practices$ = this.customMappings.practices.getRecords(translationMap);
    const brand$ = PracticeMigration.brand$(migration);
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(PROVIDER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const practitioners$ = from(brand$).pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand))),
      toINamedDocuments()
    );

    return snapshotCombineLatest([
      brand$,
      practices$,
      sourceItemCodes$,
      staff$,
      practitioners$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [brand, practices, sourceItemCodes, staff, practitioners],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            sourcePatient,
            brand,
            practices,
            sourceItemCodes,
            staff,
            practitioners,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientSterilisationJobData
  ): Promise<
    | ISterilisationRecordMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
        data.sourcePatient.data
      );
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient,
          `Couldn't resolve patient ${sourcePatientId}`
        );
      }

      const notes = await this.sourceEntities.treatmentNotes.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      );

      const sterilisationNotes =
        await this.customMappings.noteTypes.filterNotesByType(
          translationMap,
          [ItemCodeNoteType.SterilisationRecord],
          notes
        );

      const patient = await Firestore.getDoc(patientRef);

      const sterilisationRecords = await this._buildSterilisationNotes(
        sterilisationNotes,
        patient,
        data
      );

      return {
        sterilisationRecords,
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  private async _buildSterilisationNotes(
    notes: IGetRecordResponse<OasisPatientNote>[],
    patient: WithRef<IPatient>,
    data: IPatientSterilisationJobData
  ): Promise<
    (ISterilisationRecord &
      IHasSourceIdentifier & {
        createdAt: Timestamp;
      })[]
  > {
    return compact(
      await resolveSequentially(notes, async (note) => {
        const sterilisationData = this._resolveSterilisationData(note.data);

        if (!sterilisationData) {
          return;
        }

        const practiceMap = first(data.practices);
        if (!practiceMap?.destinationIdentifier) {
          throw new Error(`Can't find practice`);
        }
        const practice = await Firestore.getDoc(
          practiceMap?.destinationIdentifier
        );

        const scannedBy = note.data.practitionerId
          ? OasisNoteDataTransformer.getClinicalNotePractitioner(
              note.data.practitionerId.toString(),
              data.staff,
              data.practitioners
            )
          : undefined;

        return {
          data: sterilisationData,
          practice: toNamedDocument(practice),
          patient: toNamedDocument(patient),
          scannedBy,
          sourceIdentifier: `treatmentNote-${note.data.id.toString()}`,
          createdAt: note.record.translations.createdAt,
          status: SterilisationStatus.Passed,
          deleted: false,
        };
      })
    );
  }

  /**
   * Resolves the sterilisation record content for an OASIS patient note.
   *
   * Some OASIS practices populate the structured SN01-SN09 metadata fields,
   * while others record the autoclave details as free text in the note body
   * (DETAILS). Prefer the structured metadata when present, otherwise fall
   * back to the note body with the leading "Autoclave Record" header removed.
   *
   * Returns undefined when neither source contains any content, so the caller
   * can skip the record.
   */
  private _resolveSterilisationData(
    note: IOasisPatientNote
  ): string | undefined {
    const metadata = (note.metadata ?? [])
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0)
      .join(', ');

    if (metadata) {
      return metadata;
    }

    return this._extractNoteBodyRecords(note.note);
  }

  /**
   * Extracts autoclave record lines from a note body, dropping the leading
   * "Autoclave Record" header line and any blank lines. Returns undefined when
   * no record content remains.
   */
  private _extractNoteBodyRecords(
    noteBody: string | undefined
  ): string | undefined {
    if (!noteBody) {
      return;
    }

    const lines = noteBody
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .filter((line) => line.toLowerCase() !== 'autoclave record');

    if (lines.length === 0) {
      return;
    }

    return lines.join('\n');
  }
}
